<!-- Confirm card (chat actions, F2). The assistant proposed a state-changing tool
     but it did NOT run — the user reviews the action + its arguments here and
     confirms before it executes (POST /api/chat/tools/confirm). Reads never reach
     this; only isMutatingTool calls do. -->
<template>
  <div class="rounded-xl border border-warning/40 bg-warning-soft/40 p-3">
    <div class="flex items-center gap-2">
      <ShieldAlert class="size-4 shrink-0 text-warning" />
      <span class="text-sm font-semibold text-ink">{{ $t('chat-ui.confirm.title') }}</span>
      <code class="ml-auto truncate font-mono text-[11px] text-faint">{{ block.name }}</code>
    </div>
    <p class="mt-1 text-xs text-muted">{{ $t('chat-ui.confirm.desc') }}</p>
    <pre v-if="hasArgs" class="mt-2 max-h-44 overflow-auto rounded-md bg-code p-2 font-mono text-[11px] leading-relaxed text-code-ink">{{ pretty }}</pre>

    <div v-if="status === 'pending'" class="mt-3 flex gap-2">
      <Button size="sm" @click="run">{{ $t('chat-ui.confirm.run') }}</Button>
      <Button size="sm" variant="ghost" @click="status = 'cancelled'">{{ $t('common.cancel') }}</Button>
    </div>
    <div v-else-if="status === 'running'" class="mt-3 flex items-center gap-1.5 text-xs text-muted">
      <Loader2 class="size-3.5 animate-spin" />{{ $t('chat-ui.confirm.running') }}
    </div>
    <div v-else-if="status === 'done'" class="mt-3 flex items-center gap-1.5 text-xs font-medium text-success">
      <Check class="size-3.5" />{{ $t('chat-ui.confirm.done') }}
    </div>
    <div v-else-if="status === 'cancelled'" class="mt-3 text-xs text-faint">{{ $t('chat-ui.confirm.cancelled') }}</div>
    <div v-else-if="status === 'error'" class="mt-3 flex items-start gap-1.5 text-xs text-danger">
      <X class="mt-px size-3.5 shrink-0" /><span>{{ errorDetail }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { ShieldAlert, Check, X, Loader2 } from 'lucide-vue-next';
import Button from '../Button.vue';
import { confirmAndContinue } from '../../composables/useChatConfirm';
import type { TraceBlock } from '../../stores/chat';

const props = defineProps<{ block: TraceBlock }>();

const status = ref<'pending' | 'running' | 'done' | 'cancelled' | 'error'>('pending');
const errorDetail = ref('');

const hasArgs = computed(() => props.block.input && Object.keys(props.block.input).length > 0);
const pretty = computed(() => JSON.stringify(props.block.input ?? {}, null, 2));

async function run(): Promise<void> {
  if (!props.block.name) return;
  status.value = 'running';
  // Runs the tool AND resumes the turn — the assistant's acknowledgment streams
  // in as a new message below. We reflect the tool outcome on the card.
  const r = await confirmAndContinue(props.block);
  if (r.ok) { status.value = 'done'; }
  else { errorDetail.value = r.detail ?? 'error'; status.value = 'error'; }
}
</script>
